import type { ReactNode } from "react";

import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

/**
 * Full-height list page shell (the Action Items pattern): an optional filter
 * sidebar in a bordered card next to a content card, together sized to fill
 * the viewport below whatever is already rendered above the shell (ref-based
 * height calc — no guessed calc(100vh - Xrem) offsets).
 *
 * The shell owns the height and the two containers; the caller owns the
 * scroller inside the content card — <ScrollArea> for regular lists, a
 * virtualized list (which brings its own scroller) for large ones. That split
 * lets one shell serve both worlds.
 */
export const FullHeightListLayout = ({
  sidebar,
  header,
  children,
  footer,
}: FullHeightListLayoutProps) => (
  <div
    className="flex flex-row gap-6"
    style={{ height: "calc(100dvh - var(--list-page-offset, 7rem))" }}
    ref={(el) => {
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      el.style.setProperty("--list-page-offset", `${top + 16}px`);
      el.style.height = `calc(100dvh - ${top + 16}px)`;
    }}
  >
    {sidebar ? (
      <ScrollArea className="shrink-0 w-fit min-w-52 max-w-64 rounded-xl border bg-card p-4">
        {sidebar}
      </ScrollArea>
    ) : null}
    <Card className="py-0 flex-1 overflow-hidden flex flex-col">
      {header}
      {children}
      {footer}
    </Card>
  </div>
);

export interface FullHeightListLayoutProps {
  /** Filter sidebar content; rendered in the bordered ScrollArea container. */
  sidebar?: ReactNode;
  /**
   * Fixed strip at the top of the content card, above the scroller (e.g. the
   * active-filter bar). Brings its own styling, so a header that renders
   * nothing leaves no trace.
   */
  header?: ReactNode;
  /** Content card body — bring your own scroller (ScrollArea or virtualized list). */
  children: ReactNode;
  /** Fixed footer inside the content card, below the scroll region (e.g. a bulk bar). */
  footer?: ReactNode;
}
